import React from 'react';
import { Globe, Plus, Check, X, ArrowUp, ArrowDown } from 'lucide-react';
import { Switch } from '../../ui/Switch';
import { AVAILABLE_LANGUAGES } from '../../../constants/availableLanguages';
import { MultilingualSettings, Language } from '../../../types/multilingualSettings'; 

interface LanguageSettingsProps {
  settings: MultilingualSettings;
  onToggleEnabled: () => Promise<void>;
  onToggleLanguage: (code: string) => Promise<void>;
  onSetDefaultLanguage: (code: string) => Promise<void>;
  onAddLanguage: (language: Language) => Promise<void>;
  onRemoveLanguage: (code: string) => Promise<void>;
  onEditLanguage: (language: Language) => void;
  isLoading?: boolean;
}

export function LanguageSettings({ 
  settings, 
  onToggleEnabled, 
  onToggleLanguage, 
  onSetDefaultLanguage,
  onAddLanguage,
  onRemoveLanguage,
  onEditLanguage,
  isLoading = false
}: LanguageSettingsProps) {
  const [showLanguageSelector, setShowLanguageSelector] = React.useState(false);
  
  // Filter out already added languages
  const availableLanguages = AVAILABLE_LANGUAGES.filter(
    (lang: Language) => !settings.languages.some((existingLang: Language) => existingLang.code === lang.code)
  );

  return (
    <div className="space-y-6">
      {/* Activation générale */}
      <div className="flex items-center justify-between bg-white/5 p-4 rounded-xl border border-white/10">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-500/20">
            <Globe className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h3 className="text-white font-medium">Multilingual support</h3>
            <p className="text-gray-400 text-sm">Enable support for multiple languages</p>
          </div>
        </div>
        <Switch 
          checked={settings.enabled} 
          onChange={onToggleEnabled}
          disabled={isLoading}
        />
      </div>

      {/* Liste des langues */}
      {settings.enabled && (
        <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
          <div className="p-4 border-b border-white/10">
            <h3 className="text-white font-medium">Languages</h3>
            <p className="text-gray-400 text-sm">Configure the languages available in your chatbot</p>
          </div>

          <div className="divide-y divide-white/10">
            {settings.languages.map((language: Language) => (
              <div 
                key={language.code} 
                className="p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">{language.flag}</span>
                  <span className="text-white">{language.label}</span>
                  {language.code === settings.defaultLanguage && (
                    <span className="bg-blue-500/20 text-blue-400 text-xs px-2 py-0.5 rounded-full">
                      Default
                    </span>
                  )}
                </div>
                
                <div className="flex items-center gap-2">
                  {/* Make default button */}
                  {language.code !== settings.defaultLanguage && (
                    <button
                      onClick={() => onSetDefaultLanguage(language.code)}
                      disabled={isLoading}
                      className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                      title="Set as default language"
                    >
                      <ArrowUp className="w-4 h-4" />
                    </button>
                  )}
                  
                  {/* Edit translations button */}
                  <button
                    onClick={() => onEditLanguage(language)}
                    disabled={isLoading}
                    className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-blue-500/20 transition-colors"
                    title="Edit translations"
                  >
                    <Globe className="w-4 h-4" />
                  </button>
                  
                  {/* Toggle active */}
                  <Switch
                    checked={settings.activeLanguages.includes(language.code)}
                    onChange={() => onToggleLanguage(language.code)}
                    disabled={isLoading || language.code === settings.defaultLanguage}
                  />
                  
                  {/* Remove language */}
                  {language.code !== settings.defaultLanguage && (
                    <button
                      onClick={() => onRemoveLanguage(language.code)}
                      disabled={isLoading}
                      className="p-2 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                      title="Remove language"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          {/* Add language button */}
          <div className="p-4 border-t border-white/10">
            <div className="relative">
              <button
                onClick={() => setShowLanguageSelector(!showLanguageSelector)}
                disabled={isLoading || availableLanguages.length === 0}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-colors disabled:opacity-50"
              >
                <Plus className="w-4 h-4" />
                Add language
              </button>
              
              {showLanguageSelector && availableLanguages.length > 0 && (
                <div className="absolute bottom-full left-0 right-0 mb-2 bg-gray-800 rounded-lg border border-white/10 shadow-lg max-h-60 overflow-y-auto">
                  <div className="p-2 border-b border-white/10 sticky top-0 bg-gray-800 z-10">
                    <h4 className="text-sm font-medium text-white">Available languages</h4>
                  </div>
                  <div className="divide-y divide-white/10">
                    {availableLanguages.map((language: Language) => (
                      <button
                        key={language.code}
                        onClick={() => {
                          onAddLanguage(language);
                          setShowLanguageSelector(false);
                        }}
                        className="w-full flex items-center gap-3 p-3 hover:bg-white/5 transition-colors text-left"
                      >
                        <span className="text-xl">{language.flag}</span>
                        <span className="text-white">{language.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 